import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import styled from 'styled-components'
import { useMutation } from '@tanstack/react-query'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { formToObject } from 'lib/forms/formToObject'
import registerBusiness from 'businessTraveller/api/registerBusiness'
import requestDemo from 'businessTraveller/api/requestPostRequestDemo'
import { RequestDemoData } from 'businessTraveller/components/demo-form/RequestDemoForm'
import useUserUpdateMutation from 'businessTraveller/hooks/useUserUpdateMutation'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_REGISTRATION } from 'actions/apiActionConstants'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import { pushWithRegion } from 'actions/NavigationActions'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE } from 'components/App/Routes/routeConstants'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { createDatadogLoggerWithContext } from 'services/datadogRum'
import { sha256 } from 'lib/utils/createHashUtils'
import { getSydneyTimestamp } from 'lib/datetime/dateUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { registerBusinessTravellerSignupAccounts, BusinessTravellerSignupRegistrationResult } from 'businessTraveller/pages/business-signup/utils/registerBusinessTravellerSignupAccounts'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const CreatePasswordForm = styled.form`
  position: relative;
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  account_details: 'complete',
  create_password: 'active',
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

interface PasswordFormData {
  password: string
  confirmPassword: string
}

function mapSignupFormToDemoRequest(formData: App.BusinessTraveller.BusinessSignupFormData, userEmail: string): RequestDemoData {
  return {
    fullName: `${formData.adminDetails.firstName} ${formData.adminDetails.lastName}`,
    mobileNumber: `${formData.businessDetails.phone.prefix} ${formData.businessDetails.phone.phone}`,
    positionInCompany: 'Decision Maker',
    workEmail: userEmail,
    companyName: formData.businessDetails.name,
    businessLocation: formData.businessDetails.country,
    roleInTravelProgram: 'Decision Maker',
    companyTravelSize: formData.businessDetails.numberOfEmployees,
    currentTravelSolution: 'Other',
    message: 'Request demo via signup',
  }
}

function BusinessTravellerSignupCreatePassword(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const { adminEmail, businessName, accountDetails, creditCustomerAccessToken, creditCustomerEmail, creditCustomerId } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null)

  const business = useAppSelector((state) => state.businessTraveller.currentBusinessAccount?.business)

  const logWithContext = createDatadogLoggerWithContext({
    businessName,
  })

  const demoRequestMutation = useMutation({
    mutationFn: requestDemo,
  })

  const userUpdateMutation = useUserUpdateMutation({})

  const businessRegistrationMutation = useMutation({
    mutationFn: registerBusiness,
    onSuccess: (_, businessData) => {
      dispatch(fetchAccountBusiness({ refetch: true }))
      dispatch(pushWithRegion(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE))

      if (businessData.extraBusinessDetails?.demoRequested) {
        const demoRequestData = mapSignupFormToDemoRequest(businessData, adminEmail)
        demoRequestMutation.mutate(demoRequestData)
      }

      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure, businessData) => {
      logWithContext('Error submitting business', 'error', { errorMessage: error.message, error: JSON.stringify(error), logTimestamp: getSydneyTimestamp() })
      // TODO(LEBT-2821): both auth accounts already exist by this point, so a failure here orphans the
      // LE + LEBT accounts with no business, and a retry dead-ends on "LEBT user already exists". The
      // proper fix is an idempotent server-side signup saga in svc-business (create-or-get each step).
      if (isDedicatedTenantRegistrationEnabled) {
        logWithContext('Business signup left orphaned auth accounts after business creation failed', 'error', {
          creditCustomerId: businessData.adminDetails.creditCustomerId,
          emailHash: sha256(adminEmail.toLowerCase().trim()),
          logTimestamp: getSydneyTimestamp(),
        })
      }
      showSnackbar(error.message, 'critical', { heading: 'Error submitting business' })
      setIsFormSubmitting(false)
    },
  })

  // accountDetails is guaranteed by the guard in handleSubmit
  const registrationMutation = useMutation<BusinessTravellerSignupRegistrationResult, App.ApiCallFailure | Error, string>({
    mutationFn: async(password: string) => {
      return registerBusinessTravellerSignupAccounts({
        accountDetails: accountDetails!,
        adminEmail,
        creditCustomerAccessToken,
        creditCustomerEmail,
        creditCustomerId,
        isDedicatedTenantRegistrationEnabled,
        password,
        regionCode: currentRegionCode,
      })
    },
    onSuccess: (data) => {
      dispatch({
        type: API_CALL_SUCCESS,
        api: USER_REGISTRATION,
        data: data.lebtAccountRegistration,
        source: 'email',
      })

      userUpdateMutation.mutate({
        title: accountDetails!.adminDetails.title,
        givenName: accountDetails!.adminDetails.firstName,
        surname: accountDetails!.adminDetails.lastName,
        dob: accountDetails!.adminDetails.dob,
        postcode: accountDetails!.adminDetails.postcode,
        phone: accountDetails!.businessDetails.phone.phone,
        phonePrefix: accountDetails!.businessDetails.phone.prefix,
      }, {
        onSuccess: () => {
          businessRegistrationMutation.mutate({
            ...accountDetails!,
            adminDetails: {
              ...accountDetails!.adminDetails,
              creditCustomerAccessToken: data.creditCustomerAccessToken,
              creditCustomerEmail: data.creditCustomerEmail,
              creditCustomerId: data.creditCustomerId,
            },
          })
        },
        onError: (error: App.ApiCallFailure) => {
          // Same orphaned-accounts state as the business-creation failure below (both auth accounts
          // exist, no business, retry dead-ends) — see the TODO there for the proper fix.
          if (isDedicatedTenantRegistrationEnabled) {
            logWithContext('Business signup left orphaned auth accounts after profile update failed', 'error', {
              creditCustomerId: data.creditCustomerId,
              emailHash: sha256(adminEmail.toLowerCase().trim()),
              logTimestamp: getSydneyTimestamp(),
            })
          }
          showSnackbar(error.message, 'critical', { heading: 'Error updating profile' })
          setIsFormSubmitting(false)
        },
      })
    },
    onError: (error: App.ApiCallFailure | Error) => {
      passwordInputRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    passwordInputRef.current?.reportValidity()
  }, [])

  const confirmPasswordOnChange = useCallback(() => {
    confirmPasswordInputRef.current?.setCustomValidity('')
    confirmPasswordInputRef.current?.reportValidity()
  }, [])

  const onBackButtonClick = useCallback(() => {
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
      step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.ACCOUNT_DETAILS,
    })
  }, [accountSetupDispatch])

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const formData = formToObject<PasswordFormData>(e.currentTarget)

    if (formData.password !== formData.confirmPassword) {
      confirmPasswordInputRef.current?.setCustomValidity('Passwords do not match. Please ensure both fields are the same.')
      confirmPasswordInputRef.current?.reportValidity()
      return
    }

    if (!accountDetails) {
      showSnackbar('Account details missing. Please go back and try again.', 'critical')
      return
    }

    setIsFormSubmitting(true)
    registrationMutation.mutate(formData.password)
  }, [accountDetails, registrationMutation])

  useEffect(() => {
    if (business) {
      dispatch(pushWithRegion(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business])

  return (
    <SignupContainer>
      <CreatePasswordForm onSubmit={handleSubmit} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" verticalAlign="center">
            <IconButton
              kind="tertiary"
              variant="dark"
              onClick={onBackButtonClick}
              horizontalOutdent="start"
            >
              <LineArrowLeftIcon />
            </IconButton>
            <Heading variant="heading3">Create your password</Heading>
          </Group>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses} />
          <BodyText variant="medium">Create your password for {adminEmail}</BodyText>
          <Group direction="vertical" gap={8}>
            <PasswordInput
              autoComplete="new-password"
              label="Password"
              minLength={8}
              name="password"
              onChange={passwordOnChange}
              ref={passwordInputRef}
              required
              requiredErrorMessage="Please enter a password"
            />
            <PasswordInput
              autoComplete="new-password"
              label="Confirm password"
              minLength={8}
              name="confirmPassword"
              onChange={confirmPasswordOnChange}
              ref={confirmPasswordInputRef}
              required
              requiredErrorMessage="Please confirm password"
            />
            <Group direction="horizontal" horizontalAlign="end">
              <TextButton
                fit="mobile-full-width"
                kind="primary"
                shape="square"
                size="medium"
                type="submit"
                disabled={isFormSubmitting}
              >
                Create account
              </TextButton>
            </Group>
          </Group>
        </Group>
      </CreatePasswordForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupCreatePassword
